import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import FetchWorkstationsControl from "~/components/FetchWorkstationsControl";
import {
  setAndonViewOption,
  setEditModeEnabled
} from "~/features/andon/andon.slice";
import {
  AndonBoardLayoutItem,
  BoardLayout
} from "~/features/andon/andon.types";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";

import AddGridControl from "./AddGridControl";
import GridFlaggedBinsChip from "./GridFlaggedBinsChip";

type AndonBoardToolbarProps = {
  isFullScreen: boolean;
  editModeEnabled: boolean;
  an_boardLayouts: BoardLayout[];
  selectedLayoutName: string | null;
  workingLayoutItems: AndonBoardLayoutItem[];
  workingLayoutName: string | null;
  savedLayout: { name: string | null } | null;
  hasLayoutChanges: () => boolean;
  fetchWorkstationsInterval: number;
  workingWorkstationIds: string[];
  siteFulfillmentCenterId?: string | null;
  loadWorkingLayout: (layout: BoardLayout) => void;
  handleDeleteLayout: (layoutName: string) => Promise<void>;
  handleSaveLayout: (layoutName: string, isRename: boolean) => Promise<void>;
  handleAddWorkstations: (workstationIds: string[], fcId?: string) => void;
  handleClearLayout: () => void;
  handleDiscardChanges: () => void;
  onOpenLayoutEditor: () => void;
  onOpenSaveLayoutModal: () => void;
};

export default function AndonBoardToolbar({
  isFullScreen,
  editModeEnabled,
  an_boardLayouts,
  selectedLayoutName,
  workingLayoutItems,
  workingLayoutName,
  savedLayout,
  hasLayoutChanges,
  fetchWorkstationsInterval,
  workingWorkstationIds,
  siteFulfillmentCenterId,
  loadWorkingLayout,
  handleDeleteLayout,
  handleSaveLayout,
  handleAddWorkstations,
  handleClearLayout,
  handleDiscardChanges,
  onOpenLayoutEditor,
  onOpenSaveLayoutModal
}: AndonBoardToolbarProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: autostoreGrids } = useGetAutostoreGridsQuery();

  if (isFullScreen) {
    return (
      <Box
        sx={{
          height: 48,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          px: 2,
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            🚩 {t("bins flagged for maintenance")}:
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {(autostoreGrids ?? []).map((grid) => (
              <GridFlaggedBinsChip
                key={grid.autostoreGridId}
                grid={grid}
                pollingInterval={fetchWorkstationsInterval}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      justifyContent="space-between"
      flexShrink={0}
    >
      <Box display="flex" gap={1} alignItems="center">
        {!!an_boardLayouts.length && (
          <FormControl
            variant="outlined"
            size="small"
            sx={{ minWidth: 260, ml: 2 }}
          >
            <Select
              value={selectedLayoutName ?? ""}
              displayEmpty
              size="small"
              sx={{ height: "40px" }}
              onChange={(event) => {
                const layoutName = event.target.value;
                if (!layoutName) return;
                const selectedLayout = an_boardLayouts.find(
                  (layout) => layout.name === layoutName
                );
                if (selectedLayout) {
                  loadWorkingLayout(selectedLayout);
                }
              }}
              renderValue={(val) => {
                if (val) {
                  return (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "baseline",
                        maxWidth: "100%",
                        overflow: "hidden"
                      }}
                    >
                      <Typography component="span" noWrap>
                        {val}
                      </Typography>
                      <Typography
                        component="span"
                        color="text.secondary"
                        sx={{
                          fontStyle: "italic",
                          flexShrink: 0,
                          visibility: hasLayoutChanges() ? "visible" : "hidden"
                        }}
                      >
                        {" "}
                        – {t("edited")}
                      </Typography>
                    </Box>
                  );
                }
                if (workingLayoutItems.length > 0) {
                  return (
                    <Typography fontStyle="italic">
                      {t("new layout")}
                    </Typography>
                  );
                }
                return (
                  <Typography color="text.disabled">
                    {t("select a layout...")}
                  </Typography>
                );
              }}
            >
              {an_boardLayouts.map((layout) => (
                <MenuItem
                  key={layout.name}
                  value={layout.name}
                  sx={{ pr: 6 }}
                  onClick={() => {
                    loadWorkingLayout(layout);
                  }}
                >
                  <ListItemText primary={layout.name} />
                  {editModeEnabled && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`${t("delete")} ${layout.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeleteLayout(layout.name);
                        }}
                        sx={{
                          opacity: 0.5,
                          "&:hover": { opacity: 1, color: "error.main" }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <ProgressButton
          emphasis="high"
          responsive
          variant="contained"
          color="secondary"
          sx={{ marginLeft: "10px" }}
          disabled={!workingLayoutItems.length}
          onClick={() => {
            dispatch(setAndonViewOption("board fullscreen"));
          }}
        >
          {t("full screen")}
        </ProgressButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={editModeEnabled}
              onChange={(e) => dispatch(setEditModeEnabled(e.target.checked))}
              size="small"
              inputProps={{
                "aria-label": "Edit Mode Enabled"
              }}
            />
          }
          label={<Typography variant="body2">{t("edit mode")}</Typography>}
        />

        {editModeEnabled && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <FetchWorkstationsControl />
            <ProgressButton
              emphasis="high"
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<SettingsIcon />}
              onClick={onOpenLayoutEditor}
            >
              {t("settings")}
            </ProgressButton>
            <ProgressButton
              emphasis="high"
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => {
                if (!savedLayout && workingLayoutName?.trim()) {
                  void handleSaveLayout(workingLayoutName.trim(), false);
                } else {
                  onOpenSaveLayoutModal();
                }
              }}
              startIcon={<SaveIcon />}
              disabled={!workingLayoutItems.length || !hasLayoutChanges()}
            >
              {t("save")}
            </ProgressButton>
            <AddGridControl
              onAddWorkstations={handleAddWorkstations}
              siteFcId={siteFulfillmentCenterId ?? undefined}
              workingWorkstationIds={workingWorkstationIds}
            />
            <ProgressButton
              emphasis="high"
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<ClearIcon />}
              disabled={!workingLayoutItems.length}
              onClick={handleClearLayout}
            >
              {t("clear")}
            </ProgressButton>
            <ProgressButton
              emphasis="high"
              size="small"
              variant="contained"
              color="secondary"
              disabled={!hasLayoutChanges()}
              onClick={handleDiscardChanges}
            >
              {t("discard changes")}
            </ProgressButton>
          </>
        )}
      </Box>

      {!editModeEnabled && (
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            🚩 {t("bins flagged for maintenance")}:
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {(autostoreGrids ?? []).map((grid) => (
              <GridFlaggedBinsChip
                key={grid.autostoreGridId}
                grid={grid}
                pollingInterval={fetchWorkstationsInterval}
              />
            ))}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
